/*
 * Copyright (c) Dito Technologies LLC
 */

import React, {Dispatch} from "react";
import {Action, SetVerifyNotesStepStatus, State, StepStatus} from "ui/feature/root/FormReducer";
import UseNoteSelectAvailable from "ui/feature/root/note-select/UseNoteSelectAvailable";
import CompletePreviousStepsSection from "ui/feature/root/common/CompletePreviousStepsSection";
import UsePollNoteFinderSteps from "ui/feature/root/note-select/UsePollNoteFinderSteps";
import ProcessingSection from "ui/feature/root/common/ProcessingSection";
import Body from "ui/kit/typography/Body";
import NoteFinderPage from "ui/feature/root/note-select/NoteFinderPage";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    NoteSelectWorkspace_piece$data
} from "ui/feature/root/note-select/__generated__/NoteSelectWorkspace_piece.graphql";

interface Props {
    state: State;
    dispatch: Dispatch<Action>;
    retry: (() => void) | null;
    piece: NoteSelectWorkspace_piece$data | null;
}

const NoteSelectWorkspace: React.FC<Props> = ({piece, state, dispatch, retry}) => {
    const noteSelectAvailable = UseNoteSelectAvailable(state);
    UsePollNoteFinderSteps(state, piece, retry)

    // make sure that the state of the note finder is correct.
    React.useEffect(() => {
        if (!piece) {
            return;
        }
        const sheets = piece.sheets;
        if (!sheets) {
            return;
        }
        if (sheets) {
            let totalNumberOfPagesSuccess = 0;
            for (const page of sheets) {
                const isSuccess = state.noteFinderUserConfirmationSuccessPageIds.includes(page.id);
                if (isSuccess) {
                    totalNumberOfPagesSuccess += 1;
                }
            }
            if (totalNumberOfPagesSuccess === 0) {
                const action: SetVerifyNotesStepStatus = {
                    type: "set_verify_notes_step_status",
                    status: StepStatus.NEEDS_EDIT
                }
                dispatch(action);
            } else if (totalNumberOfPagesSuccess === sheets.length) {
                const action: SetVerifyNotesStepStatus = {
                    type: "set_verify_notes_step_status",
                    status: StepStatus.SUCCESS
                }
                dispatch(action);
            } else {
                const action: SetVerifyNotesStepStatus = {
                    type: "set_verify_notes_step_status",
                    status: StepStatus.IN_PROGRESS
                }
                dispatch(action);
            }
        } else {
            const action: SetVerifyNotesStepStatus = {
                type: "set_verify_notes_step_status",
                status: StepStatus.NEEDS_EDIT
            }
            dispatch(action);
        }
    }, [dispatch, piece, state.noteFinderUserConfirmationSuccessPageIds]);

    console.log("have piece", piece);

    if (noteSelectAvailable) {
        if (!piece || !piece.sheets) {
            console.log("processing section", piece);
            return <ProcessingSection/>;
        }
        return (<div>
            <Body>Ensure that most of the notes are correct and delete or change any that don't make sense. Don't worry
                if there are some mistakes. You will be able to manually fix these in the next step.</Body>
            {piece.sheets.map((page) => <NoteFinderPage state={state} dispatch={dispatch}
                                                           key={page.id}
                                                           sheet={page}
                                                           />)}
        </div>);
    } else {
        return (
            <CompletePreviousStepsSection/>
        );
    }
}

export default createFragmentContainer(NoteSelectWorkspace, {
    piece: graphql`
        fragment NoteSelectWorkspace_piece on MusicPiece {
            id
            sheets {
                id
                note_combine_steps: steps(step_type: NOTE_DATA_SOURCE_COMBINE) {
                    id
                    status
                }
                ...NoteFinderPage_sheet
            }
        }
    `
});
